import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getInvoicePreview = createAsyncThunk(
  "invoice/getInvoicePreview",
  async ({ salesOrderId, shipmentId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.PREVIEW(salesOrderId, shipmentId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getNextInvoiceNumber = createAsyncThunk(
  "invoice/getNextInvoiceNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_NEXT_INVOICE_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const createInvoice = createAsyncThunk(
  "invoice/createInvoice",
  async (payload, { rejectWithValue }) => {
    try {
      // New unified endpoint - SalesOrderId is now part of the request body
      const { data } = await axios.post(API_URL.INVOICE.CREATE, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateInvoice = createAsyncThunk(
  "invoice/updateInvoice",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.INVOICE.UPDATE, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateInvoiceStatus = createAsyncThunk(
  "invoice/updateInvoiceStatus",
  async (payload, { rejectWithValue }) => {
    try {
      // payload: { invoiceId, newStatus, remarks }
      const { data } = await axios.put(API_URL.INVOICE.UPDATE_STATUS, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Unified cancel for Posted invoices that may have an active IRN / E-Way Bill.
// Backend cascades: standalone EWB cancel → IRN+linked EWB cancel → invoice status flip.
// Aborts on any NIC/MI failure to keep state consistent.
// payload: { invoiceId, cancelReason, cancelRemarks }
export const cancelInvoiceWithEInvoice = createAsyncThunk(
  "invoice/cancelInvoiceWithEInvoice",
  async ({ invoiceId, cancelReason, cancelRemarks }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.INVOICE.CANCEL_WITH_EINVOICE(invoiceId),
        { cancelReason, cancelRemarks },
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPendingInvoiceSalesOrders = createAsyncThunk(
  "invoice/getPendingInvoiceSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setPendingSOActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.INVOICE.PENDING_SALES_ORDERS}?${queryString}`,
      );
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getInvoices = createAsyncThunk(
  "invoice/getInvoices",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      // Use new unified invoice list endpoint
      const { data } = await axios.get(
        `${API_URL.INVOICE.LIST}?${queryString}`,
      );
      const invoice_list = data.data.list || data.data || [];
      const totalRecords = data.data.totalRecords || 0;
      return { invoice_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getInvoiceById = createAsyncThunk(
  "invoice/getInvoiceById",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_FOR_EDIT(invoiceId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceDetail = createAsyncThunk(
  "invoice/getInvoiceDetail",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_BY_ID(invoiceId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceItems = createAsyncThunk(
  "invoice/getInvoiceItems",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_ITEMS(invoiceId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoicePaymentSummary = createAsyncThunk(
  "invoice/getInvoicePaymentSummary",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_PAYMENT_SUMMARY(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoicePayments = createAsyncThunk(
  "invoice/getInvoicePayments",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_PAYMENTS(invoiceId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceTransactionHistory = createAsyncThunk(
  "invoice/getInvoiceTransactionHistory",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_TRANSACTION_HISTORY(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getRecordPaymentInfo = createAsyncThunk(
  "invoice/getRecordPaymentInfo",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_RECORD_PAYMENT_INFO(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const recordPayment = createAsyncThunk(
  "invoice/recordPayment",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.INVOICE.RECORD_PAYMENT,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getNextPaymentNumber = createAsyncThunk(
  "invoice/getNextPaymentNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.INVOICE.GET_NEXT_PAYMENT_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getWriteOffDetails = createAsyncThunk(
  "invoice/getWriteOffDetails",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_WRITE_OFF_DETAILS(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const writeOff = createAsyncThunk(
  "invoice/writeOff",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.INVOICE.WRITE_OFF, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getNextWriteOffNumber = createAsyncThunk(
  "invoice/getNextWriteOffNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_NEXT_WRITEOFF_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceEligibleSalesOrders = createAsyncThunk(
  "invoice/getInvoiceEligibleSalesOrders",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        params.append(key, value);
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.INVOICE.INVOICE_ELIGIBLE_SALES_ORDERS}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getEligibleShipmentsForInvoice = createAsyncThunk(
  "invoice/getEligibleShipmentsForInvoice",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SHIPMENT.ELIGIBLE_FOR_INVOICE(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoicePreviewByDC = createAsyncThunk(
  "invoice/getInvoicePreviewByDC",
  async ({ salesOrderId, deliveryChallanId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.PREVIEW_BY_DC(salesOrderId, deliveryChallanId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getEligibleDCsForInvoice = createAsyncThunk(
  "invoice/getEligibleDCsForInvoice",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.DELIVERY_CHALLAN.ELIGIBLE_FOR_INVOICE(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoicePendingStatus = createAsyncThunk(
  "invoice/getInvoicePendingStatus",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.INVOICE.GET_PENDING_STATUS(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceCreditSummary = createAsyncThunk(
  "invoice/getInvoiceCreditSummary",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_CREDIT_NOTE.SUMMARY_BY_INVOICE(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceFilterOptionsByCustomer = createAsyncThunk(
  "invoice/getInvoiceFilterOptionsByCustomer",
  async ({ customerId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.INVOICE.FILTER_OPTIONS_BY_CUSTOMER}?CustomerId=${customerId}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getEligibleDCsByCustomer = createAsyncThunk(
  "invoice/getEligibleDCsByCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        params.append(key, value);
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.INVOICE.ELIGIBLE_DCS_BY_CUSTOMER}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getEligibleShipmentsByCustomer = createAsyncThunk(
  "invoice/getEligibleShipmentsByCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        params.append(key, value);
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.INVOICE.ELIGIBLE_SHIPMENTS_BY_CUSTOMER}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getMultiSourceInvoicePreview = createAsyncThunk(
  "invoice/getMultiSourceInvoicePreview",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.INVOICE.PREVIEW_MULTI,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getMrpVariants = createAsyncThunk(
  "invoice/getMrpVariants",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.INVOICE.MRP_VARIANTS,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  invoice_list: [],
  invoice_detail: null,
  invoice_items: [],
  invoice_payment_summary: null,
  invoice_payments: [],
  invoice_transaction_history: [],
  record_payment_info: null,
  write_off_details: null,
  invoice_preview: null,
  next_invoice_number: null,
  next_payment_number: null,
  next_writeoff_number: null,
  total_records: 0,
  active_payload: null,
  pending_invoice_so_list: [],
  pending_invoice_so_total_records: 0,
  pending_invoice_so_active_payload: null,
  current_values: null,
  loading: false,
  list_loading: false,
  detail_loading: false,
  items_loading: false,
  payments_loading: false,
  transaction_history_loading: false,
  record_payment_info_loading: false,
  write_off_details_loading: false,
  eligible_sales_orders: [],
  eligible_sales_orders_loading: false,
  eligible_shipments: [],
  eligible_shipments_loading: false,
  eligible_dcs: [],
  eligible_dcs_loading: false,
  invoice_pending_status: null,
  invoice_pending_status_loading: false,
  invoice_credit_summary: null,
  invoice_credit_summary_loading: false,
  invoice_filter_options: null,
  invoice_filter_options_loading: false,
  eligible_dcs_by_customer: { list: [], totalRecords: 0 },
  eligible_dcs_by_customer_loading: false,
  eligible_shipments_by_customer: { list: [], totalRecords: 0 },
  eligible_shipments_by_customer_loading: false,
  mrp_variants: null,
  mrp_variants_loading: false,
};

const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    setPendingSOActivePayload(state, action) {
      state.pending_invoice_so_active_payload = action.payload;
    },
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    resetInvoicePreview(state) {
      state.invoice_preview = null;
    },
    resetInvoiceDetail(state) {
      state.invoice_detail = null;
      state.invoice_items = [];
      state.invoice_payment_summary = null;
      state.invoice_payments = [];
      state.invoice_transaction_history = [];
    },
    resetEligibleSalesOrders(state) {
      state.eligible_sales_orders = [];
    },
    resetEligibleShipments(state) {
      state.eligible_shipments = [];
    },
    resetEligibleDCs(state) {
      state.eligible_dcs = [];
    },
    resetRecordPaymentInfo(state) {
      state.record_payment_info = null;
    },
    resetWriteOffDetails(state) {
      state.write_off_details = null;
    },
    resetInvoicePendingStatus(state) {
      state.invoice_pending_status = null;
    },
    resetInvoiceCreditSummary(state) {
      state.invoice_credit_summary = null;
    },
    resetInvoiceFilterOptions(state) {
      state.invoice_filter_options = null;
    },
    resetEligibleDCsByCustomer(state) {
      state.eligible_dcs_by_customer = { list: [], totalRecords: 0 };
    },
    resetMrpVariants(state) {
      state.mrp_variants = null;
    },
    resetEligibleShipmentsByCustomer(state) {
      state.eligible_shipments_by_customer = { list: [], totalRecords: 0 };
    },
  },
  extraReducers: (builder) => {
    builder
      // Invoice List
      .addCase(getInvoices.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.invoice_list = action.payload.invoice_list;
        state.total_records = action.payload.data?.totalRecords || 0;
        state.list_loading = false;
      })
      .addCase(getInvoices.rejected, (state) => {
        state.list_loading = false;
      })
      // Pending Invoice Sales Orders
      .addCase(getPendingInvoiceSalesOrders.fulfilled, (state, action) => {
        state.pending_invoice_so_list = action.payload.list;
        state.pending_invoice_so_total_records =
          action.payload.data.totalRecords;
      })
      .addCase(getInvoicePreview.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoicePreview.fulfilled, (state, action) => {
        state.invoice_preview = action.payload;
        state.loading = false;
      })
      .addCase(getInvoicePreview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getNextInvoiceNumber.fulfilled, (state, action) => {
        state.next_invoice_number = action.payload;
      })
      // Invoice Detail
      .addCase(getInvoiceById.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(getInvoiceById.fulfilled, (state, action) => {
        state.invoice_detail = action.payload;
        state.detail_loading = false;
      })
      .addCase(getInvoiceById.rejected, (state) => {
        state.detail_loading = false;
      })
      // getInvoiceDetail — uses GET_BY_ID endpoint (includes e-invoice data)
      .addCase(getInvoiceDetail.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(getInvoiceDetail.fulfilled, (state, action) => {
        state.invoice_detail = action.payload;
        state.detail_loading = false;
      })
      .addCase(getInvoiceDetail.rejected, (state) => {
        state.detail_loading = false;
      })
      // Invoice Items
      .addCase(getInvoiceItems.pending, (state) => {
        state.items_loading = true;
      })
      .addCase(getInvoiceItems.fulfilled, (state, action) => {
        state.invoice_items = action.payload?.items || action.payload || [];
        state.items_loading = false;
      })
      .addCase(getInvoiceItems.rejected, (state) => {
        state.items_loading = false;
      })
      // Payment Summary
      .addCase(getInvoicePaymentSummary.fulfilled, (state, action) => {
        state.invoice_payment_summary = action.payload;
      })
      // Payments
      .addCase(getInvoicePayments.pending, (state) => {
        state.payments_loading = true;
      })
      .addCase(getInvoicePayments.fulfilled, (state, action) => {
        state.invoice_payments =
          action.payload?.payments || action.payload || [];
        state.payments_loading = false;
      })
      .addCase(getInvoicePayments.rejected, (state) => {
        state.payments_loading = false;
      })
      // Transaction History
      .addCase(getInvoiceTransactionHistory.pending, (state) => {
        state.transaction_history_loading = true;
      })
      .addCase(getInvoiceTransactionHistory.fulfilled, (state, action) => {
        state.invoice_transaction_history =
          action.payload?.items || action.payload || [];
        state.transaction_history_loading = false;
      })
      .addCase(getInvoiceTransactionHistory.rejected, (state) => {
        state.invoice_transaction_history = [];
        state.transaction_history_loading = false;
      })
      // Record Payment Info
      .addCase(getRecordPaymentInfo.pending, (state) => {
        state.record_payment_info_loading = true;
      })
      .addCase(getRecordPaymentInfo.fulfilled, (state, action) => {
        state.record_payment_info = action.payload;
        state.record_payment_info_loading = false;
      })
      .addCase(getRecordPaymentInfo.rejected, (state) => {
        state.record_payment_info = null;
        state.record_payment_info_loading = false;
      })
      // Next Payment Number
      .addCase(getNextPaymentNumber.fulfilled, (state, action) => {
        state.next_payment_number = action.payload;
      })
      // Next Write Off Number
      .addCase(getNextWriteOffNumber.fulfilled, (state, action) => {
        state.next_writeoff_number = action.payload;
      })
      // Write Off Details
      .addCase(getWriteOffDetails.pending, (state) => {
        state.write_off_details_loading = true;
      })
      .addCase(getWriteOffDetails.fulfilled, (state, action) => {
        state.write_off_details = action.payload;
        state.write_off_details_loading = false;
      })
      .addCase(getWriteOffDetails.rejected, (state) => {
        state.write_off_details = null;
        state.write_off_details_loading = false;
      })
      // Eligible Sales Orders
      .addCase(getInvoiceEligibleSalesOrders.pending, (state) => {
        state.eligible_sales_orders_loading = true;
      })
      .addCase(getInvoiceEligibleSalesOrders.fulfilled, (state, action) => {
        state.eligible_sales_orders =
          action.payload?.list || action.payload || [];
        state.eligible_sales_orders_loading = false;
      })
      .addCase(getInvoiceEligibleSalesOrders.rejected, (state) => {
        state.eligible_sales_orders = [];
        state.eligible_sales_orders_loading = false;
      })
      // Eligible Shipments for Invoice
      .addCase(getEligibleShipmentsForInvoice.pending, (state) => {
        state.eligible_shipments_loading = true;
      })
      .addCase(getEligibleShipmentsForInvoice.fulfilled, (state, action) => {
        state.eligible_shipments = action.payload || [];
        state.eligible_shipments_loading = false;
      })
      .addCase(getEligibleShipmentsForInvoice.rejected, (state) => {
        state.eligible_shipments = [];
        state.eligible_shipments_loading = false;
      })
      // Invoice Preview by Delivery Challan (writes to same invoice_preview state)
      .addCase(getInvoicePreviewByDC.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoicePreviewByDC.fulfilled, (state, action) => {
        state.invoice_preview = action.payload;
        state.loading = false;
      })
      .addCase(getInvoicePreviewByDC.rejected, (state) => {
        state.loading = false;
      })
      // Eligible DCs for Invoice
      .addCase(getEligibleDCsForInvoice.pending, (state) => {
        state.eligible_dcs_loading = true;
      })
      .addCase(getEligibleDCsForInvoice.fulfilled, (state, action) => {
        state.eligible_dcs = action.payload || [];
        state.eligible_dcs_loading = false;
      })
      .addCase(getEligibleDCsForInvoice.rejected, (state) => {
        state.eligible_dcs = [];
        state.eligible_dcs_loading = false;
      })
      // Invoice Pending Status
      .addCase(getInvoicePendingStatus.pending, (state) => {
        state.invoice_pending_status_loading = true;
      })
      .addCase(getInvoicePendingStatus.fulfilled, (state, action) => {
        state.invoice_pending_status = action.payload;
        state.invoice_pending_status_loading = false;
      })
      .addCase(getInvoicePendingStatus.rejected, (state) => {
        state.invoice_pending_status = null;
        state.invoice_pending_status_loading = false;
      })
      // Invoice Credit Summary
      .addCase(getInvoiceCreditSummary.pending, (state) => {
        state.invoice_credit_summary_loading = true;
      })
      .addCase(getInvoiceCreditSummary.fulfilled, (state, action) => {
        state.invoice_credit_summary = action.payload;
        state.invoice_credit_summary_loading = false;
      })
      .addCase(getInvoiceCreditSummary.rejected, (state) => {
        state.invoice_credit_summary = null;
        state.invoice_credit_summary_loading = false;
      })
      // Filter Options by Customer
      .addCase(getInvoiceFilterOptionsByCustomer.pending, (state) => {
        state.invoice_filter_options_loading = true;
      })
      .addCase(getInvoiceFilterOptionsByCustomer.fulfilled, (state, action) => {
        state.invoice_filter_options = action.payload;
        state.invoice_filter_options_loading = false;
      })
      .addCase(getInvoiceFilterOptionsByCustomer.rejected, (state) => {
        state.invoice_filter_options = null;
        state.invoice_filter_options_loading = false;
      })
      // Eligible DCs by Customer
      .addCase(getEligibleDCsByCustomer.pending, (state) => {
        state.eligible_dcs_by_customer_loading = true;
      })
      .addCase(getEligibleDCsByCustomer.fulfilled, (state, action) => {
        state.eligible_dcs_by_customer = {
          list: action.payload?.list || action.payload || [],
          totalRecords: action.payload?.totalRecords || 0,
        };
        state.eligible_dcs_by_customer_loading = false;
      })
      .addCase(getEligibleDCsByCustomer.rejected, (state) => {
        state.eligible_dcs_by_customer = { list: [], totalRecords: 0 };
        state.eligible_dcs_by_customer_loading = false;
      })
      // Eligible Shipments by Customer
      .addCase(getEligibleShipmentsByCustomer.pending, (state) => {
        state.eligible_shipments_by_customer_loading = true;
      })
      .addCase(getEligibleShipmentsByCustomer.fulfilled, (state, action) => {
        state.eligible_shipments_by_customer = {
          list: action.payload?.list || action.payload || [],
          totalRecords: action.payload?.totalRecords || 0,
        };
        state.eligible_shipments_by_customer_loading = false;
      })
      .addCase(getEligibleShipmentsByCustomer.rejected, (state) => {
        state.eligible_shipments_by_customer = { list: [], totalRecords: 0 };
        state.eligible_shipments_by_customer_loading = false;
      })
      // Multi Source Invoice Preview
      .addCase(getMultiSourceInvoicePreview.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMultiSourceInvoicePreview.fulfilled, (state, action) => {
        state.invoice_preview = action.payload;
        state.loading = false;
      })
      .addCase(getMultiSourceInvoicePreview.rejected, (state) => {
        state.loading = false;
      })
      // getMrpVariants
      .addCase(getMrpVariants.pending, (state) => {
        state.mrp_variants_loading = true;
      })
      .addCase(getMrpVariants.fulfilled, (state, action) => {
        state.mrp_variants_loading = false;
        state.mrp_variants = action.payload;
      })
      .addCase(getMrpVariants.rejected, (state) => {
        state.mrp_variants_loading = false;
      });
  },
});

export const {
  setActivePayload,
  setPendingSOActivePayload,
  setCurrentValues,
  resetInvoicePreview,
  resetInvoiceDetail,
  resetEligibleSalesOrders,
  resetEligibleShipments,
  resetEligibleDCs,
  resetRecordPaymentInfo,
  resetWriteOffDetails,
  resetInvoicePendingStatus,
  resetInvoiceCreditSummary,
  resetInvoiceFilterOptions,
  resetEligibleDCsByCustomer,
  resetEligibleShipmentsByCustomer,
  resetMrpVariants,
} = invoiceSlice.actions;
export default invoiceSlice.reducer;
